import { useState, useMemo } from 'react'
import { Search } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState } from '@/components/common/EmptyState'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { KnowledgeSearchResultCard } from '@/components/knowledge'
import { useKnowledgeSearch } from '@/api/knowledge'
import { useDebounce } from '@/hooks/useDebounce'
import type { KnowledgeSearchRequest, SourceType } from '@/types/knowledge'

export function KnowledgeSearchPage() {
  const [query, setQuery] = useState('')
  const [sourceType, setSourceType] = useState<string>('all')
  const [maxResults, setMaxResults] = useState('10')
  const [enableReranking, setEnableReranking] = useState(true)

  const debouncedQuery = useDebounce(query, 300)

  const searchRequest: KnowledgeSearchRequest = useMemo(() => ({
    query: debouncedQuery,
    maxResults: parseInt(maxResults, 10),
    enableReranking,
    sourceTypeFilter: sourceType !== 'all' ? [sourceType as SourceType] : undefined,
  }), [debouncedQuery, maxResults, enableReranking, sourceType])

  const { data, isLoading, error } = useKnowledgeSearch(searchRequest)

  return (
    <PageShell>
      <div className="p-6 space-y-6">
        <PageHeader
          title="Knowledge Search"
          description="Search indexed knowledge sources with hybrid retrieval"
        />

        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[240px]">
            <Input
              placeholder="Search knowledge base..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-10"
            />
          </div>

          <Select value={sourceType} onValueChange={setSourceType}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Source type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              <SelectItem value="Confluence">Confluence</SelectItem>
              <SelectItem value="AdoWiki">ADO Wiki</SelectItem>
            </SelectContent>
          </Select>

          <Select value={maxResults} onValueChange={setMaxResults}>
            <SelectTrigger className="w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5">5 results</SelectItem>
              <SelectItem value="10">10 results</SelectItem>
              <SelectItem value="20">20 results</SelectItem>
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2">
            <Switch
              id="reranking"
              checked={enableReranking}
              onCheckedChange={setEnableReranking}
            />
            <Label htmlFor="reranking" className="text-sm">Reranking</Label>
          </div>
        </div>

        {data && (
          <p className="text-sm text-muted-foreground">
            {data.totalCandidates} candidates, {data.results.length} shown in {data.queryTimeMs}ms
          </p>
        )}

        {isLoading && (
          <LoadingSpinner className="py-12" />
        )}

        {error && (
          <EmptyState
            icon={Search}
            title="Search failed"
            description="Unable to search knowledge base. Please try again."
          />
        )}

        {data && data.results.length > 0 && (
          <div className="space-y-3">
            {data.results.map((result) => (
              <KnowledgeSearchResultCard key={result.chunkId} result={result} />
            ))}
          </div>
        )}

        {data && data.results.length === 0 && debouncedQuery.trim().length > 0 && (
          <EmptyState
            icon={Search}
            title="No results found"
            description="Try different search terms or adjust your filters."
          />
        )}

        {!data && !isLoading && !error && (
          <EmptyState
            icon={Search}
            title="Search the knowledge base"
            description="Enter a query above to search across all indexed knowledge sources."
          />
        )}
      </div>
    </PageShell>
  )
}
